import { memo, useRef, MouseEventHandler, useContext, lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { Button, Checkbox, ColorSwatches, Icon, SelectionSet, Typography } from '@vp/swan'
import {
  TemplateTile,
  TemplateTileHeader,
  TemplateTileImage,
  TemplateTileContent,
  TemplateTileTitle,
  TemplateTilePricing,
  TemplateTileColorSwatchContent,
} from '@vp/ep-tiles'
import classnames from 'classnames'
import { getLocaleSelector } from '~/client/store/config'
import { LinkWithQuery } from '~/client/components/common/LinkWithQuery'
import { useFavorite } from '~/client/components/Gallery/DesignTile/hooks/useFavorite'
import { useColorSwatches } from '~/client/components/Gallery/DesignTile/hooks/useColorSwatches'
import { DesignProps } from '~/client/components/Gallery/DesignTile/interface'
import { usePricing } from '~/client/components/Gallery/DesignTile/hooks/usePricing'
import { useImage } from '~/client/components/Gallery/DesignTile/hooks/useImage'
import { DismissableAlertBox } from '~/client/components/common/DismissableAlertBox'
import { TileGreetingOptions } from '~/client/components/common/TileGreetingOptions'
import { TemplateTileColorSwatchWrapper } from '~/client/components/common/TemplateTile/components/TemplateTileColorSwatchWrapper'
import { PreviouslyOrderedCallout } from '~/client/components/common/PreviouslyOrderedCallout'
import { PricePresentationWrapper } from '~/client/components/common/PricingDisplay/PricePlatform/PricePresentationWrapper'
import { colorSwatchSelector } from '~/client/store/design/reducer'
import { resolveInitialSwatchId } from '~/shared/resolveInitialSwatchId'
import { PREVIEW_TYPE } from 'shared/constants'
import { PageId, PricePresentationType } from '@vp/price-platform'
import { useTranslations } from '~/client/hooks/useTranslations'
import { getShowSets, getDebugModeEnabled } from '~/client/store/debug'
import { SetHighlightingContext } from '~/client/components/Gallery/SetHighlighting/SetHighlightingContext'
import { RankingSetDisplay } from 'client/components/Gallery/SetHighlighting/RankingSetDisplay'

const ImageDebugActions = lazy(() => import('client/components/DebugToolbar/components/ImageDebugActions'))
const DebugPop = lazy(() => import('~/client/components/Gallery/PreviewArea/DebugPop'))

const DesignTileComponent = ({
  imageProps,
  colorSwatches,
  handleColorSwatchChange,
  market,
  pricingPresentationType,
  productOptionsByProductKey,
  quantity,
  differentialPrice,
  hideVatMessage,
  hideShippingMessageInVatMessage,
  selectedProductOptions,
  tileEntity,
  selectedDesignId,
  entityId,
  currentDesignId,
  favoriteId,
  previewType,
  noFollow,
  greetingOptions,
  greetingTitle,
  shouldShowGreetingOptions,
  shouldHideForSocialMedia,
  isPreviouslyOrdered,
  onMouseEnter,
  maxColorSwatchesPerRow,
  onLabelClick,
  refValue,
}: DesignProps) => {
  const locale = useSelector(getLocaleSelector)
  const localize = useTranslations()
  const showSets = useSelector(getShowSets)
  const debugEnabled = useSelector(getDebugModeEnabled)
  const setHighlightingContext = useContext(SetHighlightingContext)

  // Get Favorite button params
  const selectColorSwatch = useSelector(colorSwatchSelector)
  const initialSwatchId = resolveInitialSwatchId(tileEntity)
  const defaultColorSwatch = selectColorSwatch(initialSwatchId, entityId, PREVIEW_TYPE.TILE)

  const { isFavorite, onChange, ariaLabel, errorMessage, onRequestDismiss, dismissVisuallyHiddenLabel } = useFavorite({
    entityId,
    previewType,
    colorSwatches,
    favoriteId,
    tileEntity,
    defaultColorSwatch,
  })

  const { imgRef, showSpinner, alt, imageSrc, imageSrcSet, loading, onError, onLoad, accessibilityText, isError, fetchpriority, size, emptyPreviewText } =
    useImage({ imageProps })

  // Get and handle Color Swatches information
  const contentRef = useRef<HTMLDivElement>(null)
  const {
    shouldShowColorSwatches,
    onColorSwatchChangeWrapper,
    handleClickShowMoreButton,
    showMoreIcon,
    isExpanded,
    shouldShowShowMoreButton,
    isColorSwatchColorsSimilar,
  } = useColorSwatches(handleColorSwatchChange, maxColorSwatchesPerRow, colorSwatches)

  const handleStopPropagation = (e: React.MouseEvent): void => e.stopPropagation()
  const handlePreventDefault = (e: React.MouseEvent): void => e.preventDefault()

  const handleShowMoreButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    handleStopPropagation(e)
    handlePreventDefault(e)
    handleClickShowMoreButton()
    onLabelClick(e)
  }

  // Get Pricing data
  const {
    templateTileTitle,
    shouldShowComparativeNames,
    formattedPrices,
    isLoading,
    isError: isPriceError,
  } = usePricing({
    tileEntity,
    differentialPrice,
    pricingPresentationType,
    market,
    quantity,
    hideVatMessage,
    hideShippingMessageInVatMessage,
    selectedProductOptions,
    productOptionsByProductKey,
  })

  const shouldShowLoadingState = isLoading && !isPriceError

  // Calculate tile background color for ranking set highlighting (only when showSets QSP is true)
  let tileClassName = ''
  let tileStyle: React.CSSProperties | undefined
  if (showSets && tileEntity.rankingSet && setHighlightingContext) {
    const color = setHighlightingContext.getSetColor(tileEntity.rankingSet)
    if (color) {
      tileClassName = 'template-tile-set-highlighting'
      tileStyle = { '--set-color': color } as React.CSSProperties
    }
  }

  return (
    <TemplateTile className={classnames('ep-template-tile-wrapper', tileClassName)} ref={refValue} onMouseEnter={onMouseEnter} style={tileStyle}>
      <TemplateTileHeader>
        <>
          {isPreviouslyOrdered && <PreviouslyOrderedCallout className="previously-ordered-callout-tile" ml="3" />}
          {errorMessage && (
            <DismissableAlertBox
              toast
              className="template-favorite-button-alert-box"
              dismissVisuallyHiddenLabel={dismissVisuallyHiddenLabel}
              skin="error"
              onClick={handlePreventDefault}
              onRequestDismiss={onRequestDismiss}
            >
              {errorMessage}
            </DismissableAlertBox>
          )}
          <Checkbox
            aria-label={ariaLabel}
            checked={isFavorite}
            className="template-favorite-button"
            data-testid="favorite-button-for-tile"
            skin="favorite"
            onChange={onChange}
            onClick={handleStopPropagation}
          />
        </>
      </TemplateTileHeader>
      <TemplateTileImage
        accessibleTextForLoadingState={accessibilityText}
        alt={alt}
        emptyPreviewText={emptyPreviewText}
        fetchpriority={fetchpriority}
        isError={isError}
        isLoading={showSpinner}
        loading={loading}
        ref={imgRef}
        renderComponent={(p) => <LinkWithQuery {...p} designId={tileEntity.designId} currentDesignId={currentDesignId} noFollow={noFollow} />}
        size={size}
        src={imageSrc}
        srcSet={imageSrcSet}
        onError={onError}
        onLoad={onLoad}
        {...(showSpinner && { className: 'base-tile-header-wrapper' })}
      />
      <TemplateTileContent>
        {shouldShowColorSwatches && (
          <div className="color-swatches-wrapper" data-testid="color-swatches-for-tile">
            <TemplateTileColorSwatchContent>
              <SelectionSet
                aria-label={localize('ColorSwatchGroupLabel')}
                className={classnames('color-swatches', { 'show-color-swatches': isExpanded })}
                selectedValue={selectedDesignId}
                onClick={handleStopPropagation}
                onSelectedValueChange={onColorSwatchChangeWrapper}
              >
                <ColorSwatches className={classnames('color-swatches-items', { 'hide-color-swatches': !isExpanded })} ref={contentRef}>
                  {colorSwatches.map(({ designId, colorComposition = [] }) => (
                    <TemplateTileColorSwatchWrapper
                      colorComposition={colorComposition}
                      designId={designId}
                      isColorSwatchColorsSimilar={isColorSwatchColorsSimilar}
                      key={designId}
                      locale={locale}
                    />
                  ))}
                </ColorSwatches>
              </SelectionSet>
            </TemplateTileColorSwatchContent>
            {shouldShowShowMoreButton && (
              <Button className="show-more-button" skin="tertiary" onClick={handleShowMoreButtonClick}>
                <span>{colorSwatches.length}</span>
                <Icon iconType={showMoreIcon} size="24p" />
              </Button>
            )}
          </div>
        )}
        {shouldShowComparativeNames && <TemplateTileTitle>{templateTileTitle}</TemplateTileTitle>}
        {!shouldHideForSocialMedia && (
          <TemplateTilePricing>
            <PricePresentationWrapper
              formattedPrices={formattedPrices}
              isLoading={shouldShowLoadingState}
              pageId={PageId.Gallery}
              presentationType={pricingPresentationType as PricePresentationType}
            />
          </TemplateTilePricing>
        )}
        {shouldShowGreetingOptions && (
          <TileGreetingOptions>
            <Typography as="span" className="tile-greeting-title" fontWeight="bold">
              {greetingTitle}
            </Typography>
            {greetingOptions}
          </TileGreetingOptions>
        )}
        {debugEnabled && (
          <Suspense fallback={null}>
            <DebugPop entityId={tileEntity.designId} />
            <ImageDebugActions previewUrls={imageProps.previewsUrls} />
          </Suspense>
        )}
        <RankingSetDisplay entityId={tileEntity.designId} />
      </TemplateTileContent>
    </TemplateTile>
  )
}

export const DesignTile = memo(DesignTileComponent)
